// Supabase
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useEffect, useState, useRef } from 'react'

// Chart
import { Line, Bar } from 'react-chartjs-2'
import Chart from 'chart.js/auto'
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm'
import annotationPlugin from 'chartjs-plugin-annotation'
Chart.register(annotationPlugin)

import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm'

// UI
import LocationSelect from '../ui/LocationSelect'
import InvlevelHistory from '../ui/InvlevelHistory'
import ComponentInventoryProjectionEvents from './ComponentInventoryProjectionEvents'
import Box from '../ui/Box'

// Mantine
import { Select, Anchor, Button } from '@mantine/core'

// Bus
import { useListener } from 'react-bus'

// Lodash
import { sumBy, min, max, set } from 'lodash'

// Nextjs

// Hook
import useUserLocation from '@/hooks/useUserLocation'

import { invprojection, component_level_at_date } from '@/lib/invprojection'

// Classnames
import classNames from 'classnames'

export default function ComponentInventoryChart({
    component,
    fulfillmentOrders,
}) {
    const { requestGet } = useAxiosApi()

    const containerRef = useRef(null)

    const [data, setData] = useState([])

    const loadInventoryChartData = async () => {
        const { data } = await requestGet(
            `/components/${component.id}/invlevels-history`,
            { since: dayjs().subtract(6, 'month').format('YYYY-MM-DD') }
        )

        setData(data)
    }

    useEffect(() => {
        loadInventoryChartData()
    }, [])

    useListener('componentChanged', () => {
        loadInventoryChartData()
    })

    // Values
    const [invtype, setInvtype] = useState('physical')
    const [locationId, setLocationId] = useState(1)

    const locationIdNum = parseInt(locationId)

    const fulfillmentOrdersAtLocation = fulfillmentOrders.filter(
        (fo) => fo.location_id === locationIdNum
    )

    // Check location id

    const { userLocationId } = useUserLocation()

    useEffect(() => {
        // If the location change for a different location than the current one, update the location id
        if (locationId !== userLocationId) {
            setLocationId(userLocationId)
        }
    }, [userLocationId])

    // On hover
    // Chart hover
    const [tooltipLeft, setTooltipLeft] = useState(0)
    const [chartTooltip, setChartTooltip] = useState(null)
    const [tooltipPosition, setTooltipPosition] = useState(null)

    const tooltip = ({ tooltip }) => {
        const chartArea = tooltip.chart.chartArea

        const isXInChartArea =
            tooltip._eventPosition.x > chartArea.left &&
            tooltip._eventPosition.x < chartArea.right
        const isYInChartArea =
            tooltip._eventPosition.y > chartArea.top &&
            tooltip._eventPosition.y < chartArea.bottom

        const isXYInChartArea = isXInChartArea && isYInChartArea

        if (tooltip && isXYInChartArea) {
            setChartTooltip(tooltip.dataPoints)
            setTooltipLeft(tooltip._eventPosition.x)

            const position =
                tooltip._eventPosition.x > containerRef.current.offsetWidth / 2
                    ? 'left'
                    : 'right'

            setTooltipPosition(position)
        } else {
            setChartTooltip(null)
        }
    }

    // Chart
    const options = {
        locale: 'fr',
        aspectRatio: 3,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
                external: tooltip,
            },
        },
        interaction: {
            axis: 'x',
            intersect: false,
        },
        animation: false,
        scales: {
            x: {
                type: 'time',
                ticks: {
                    maxTicksLimit: 20,
                },
                time: {
                    unit: 'month',
                    format: 'YYYY-MM-DD',
                    tooltipFormat: 'YYYY-MM-DD',
                },
                grid: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                suggestedMax: 100,
                ticks: {
                    precision: 0,
                    maxTicksLimit: 5,
                    stepSize: 1,
                },
                border: {
                    dash: [5, 5],
                },
            },
        },
    }

    const dateOfToday = dayjs().format('YYYY-MM-DD')

    // Dataset
    let filteredData = data.filter(
        (i) => i.invtype === invtype && i.location_id === locationIdNum
    )

    // If no data for today, add today
    if (filteredData.length > 0) {
        if (filteredData[filteredData.length - 1].date !== dateOfToday) {
            filteredData.push({
                date: dateOfToday,
                invlevel: filteredData[filteredData.length - 1].invlevel,
            })
        }
    }

    // Projection
    const projectionLabels = []
    const projectionChartDataset = []
    const projectionAssemblableQuantity = []

    const numberOfDaysToAdd = 180
    let level = filteredData[filteredData.length - 1]?.invlevel

    const flowRate = locationIdNum === 1 ? 0 : component.flow_rate

    let dateWhereStockIsZero = null
    let dateWhereAssemblableStockIsZero = null

    const inventoryProjectionEvents = invprojection({
        component,
        fulfillment_orders_by_date: fulfillmentOrdersAtLocation,
        location_id: locationIdNum,
    })

    if (filteredData.length > 1) {
        projectionChartDataset.push(
            ...Array((filteredData.length ?? 0) - 1).fill(null),
            level
        )

        projectionAssemblableQuantity.push(
            ...Array(filteredData.length).fill(null)
        )

        for (let i = 0; i < numberOfDaysToAdd; i++) {
            // Date of projection
            const date = dayjs(dateOfToday).add(i, 'day').format('YYYY-MM-DD')

            // Calculate level
            const projectionsEventsForDate = inventoryProjectionEvents.filter(
                (event) => event.date === date || (i == 0 && event.date <= date)
            )

            const projectionQuantity = sumBy(
                projectionsEventsForDate,
                'quantity'
            )

            // Calculate level of projection
            level = level - flowRate + projectionQuantity

            // Break if level is at zero
            const hasRemainingEvents = inventoryProjectionEvents.some(
                (i) => i.date > date
            )

            // Check if stock is zero
            if (level <= 0 && dateWhereStockIsZero === null) {
                dateWhereStockIsZero = date
            }

            /*
            if (level <= 0 && !hasRemainingEvents) {
                break
            }
            */

            // Push date of projection
            projectionLabels.push(date)

            // Push data of projection
            projectionChartDataset.push(level)

            // Calculate assemblable quantity at date
            // Get level of every child component at date
            if (
                component.children.length > 0 &&
                (locationIdNum === 6 || locationIdNum === 1)
            ) {
                const levelOfEveryChildComponent = component.children.map(
                    (child) => {
                        const invlevel_at_date =
                            component_level_at_date({
                                component: child,
                                date,
                                location_id: locationIdNum,
                            }) * child.pivot.quantity

                        return invlevel_at_date
                    }
                )

                const minLevelOfEveryChild = min(levelOfEveryChildComponent)

                const assemblableQuantityLevel = max([
                    level,
                    minLevelOfEveryChild + level,
                ])

                projectionAssemblableQuantity.push(assemblableQuantityLevel)

                if (
                    assemblableQuantityLevel <= 0 &&
                    dateWhereAssemblableStockIsZero === null
                ) {
                    dateWhereAssemblableStockIsZero = date
                }
            }
        }
    }

    // Chart data

    const chartLabels = [
        ...filteredData.map((i) => i.date),
        ...projectionLabels,
    ]

    const chartDataset = filteredData.map((i) => i.invlevel)

    const dataSet = {
        label: 'Niveau',
        data: chartDataset,
        stepped: true,
        tension: 0.1,
        pointRadius: 0,
        fill: true,
        borderColor: 'rgb(33, 89, 183)',
        backgroundColor: 'rgba(33, 89, 183, 0.1)',
        pointHoverRadius: 0,
        borderWidth: 2,
        pointBackgroundColor: 'rgb(33, 89, 183)',
    }

    const projectionDataSet = {
        label: 'Projection',
        data: projectionChartDataset,
        stepped: true,
        tension: 0.1,
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
        borderColor: 'rgb(236, 72, 153)',
        backgroundColor: 'rgba(236, 72, 153, 0.1)',
        pointHoverRadius: 0,
        pointBackgroundColor: 'rgb(236, 72, 153)',
    }

    const projectionAssemblableDataSet = {
        label: 'Assemblable',
        data: projectionAssemblableQuantity,
        stepped: true,
        tension: 0.1,
        fill: false,
        pointRadius: 0,
        borderWidth: 2,
        borderColor: 'orange',
        backgroundColor: 'rgba(97, 16, 162, 0)',
        pointHoverRadius: 0,
        pointBackgroundColor: 'rgb(97, 16, 162)',
    }

    // Datasets to display
    const [displayAssembableProjection, setDisplayAssemblableProjection] =
        useState(true)

    const datasets =
        component.children.length > 0 && displayAssembableProjection
            ? [dataSet, projectionDataSet, projectionAssemblableDataSet]
            : [dataSet, projectionDataSet]

    const rightSection = (
        <div className="flex gap-2">
            <LocationSelect
                value={locationId}
                onChange={(value) => setLocationId(parseInt(value))}
            />

            {false && (
                <Select
                    data={[
                        { value: 'physical', label: 'Stock physique' },
                        {
                            value: 'available',
                            label: 'Quantité en vente',
                        },
                    ]}
                    value={invtype}
                    onChange={setInvtype}
                    variant="default"
                    allowDeselect={false}
                />
            )}

            <ComponentInventoryProjectionEvents
                inventoryProjectionEvents={inventoryProjectionEvents}
            />

            <InvlevelHistory
                component_id={component.id}
                location_id={locationIdNum}
                invtype={invtype}
                showReasonFilter
            />
        </div>
    )

    return (
        <Box
            title="Évolution"
            innerRef={containerRef}
            rightSection={rightSection}
        >
            {filteredData.length > 1 && (
                <div
                    className="relative flex flex-col"
                    onMouseLeave={() => {
                        setChartTooltip(null)
                    }}
                >
                    <div className="relative pt-10 w-full aspect-[2]">
                        {chartTooltip && (
                            <div
                                className="absolute top-0 bottom-[29px] border-l border-l-[3px] border-dashed border-black pointer-events-none"
                                style={{
                                    left: tooltipLeft,
                                }}
                            />
                        )}

                        <Line
                            options={options}
                            data={{
                                labels: chartLabels,
                                datasets: datasets,
                            }}
                        />
                    </div>

                    {chartTooltip && (
                        <div
                            style={{
                                top: 0,
                                left: tooltipLeft,
                            }}
                            className={classNames(
                                'min-w-[200px] absolute bg-black flex flex-col text-white transform p-2 font-medium whitespace-nowrap',
                                {
                                    '-translate-x-[calc(100%-3px)]':
                                        tooltipPosition === 'left',
                                }
                            )}
                        >
                            <span>
                                {dayjs(chartTooltip[0].label).format('LL')}
                            </span>

                            <div>
                                {chartTooltip.map((point, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2"
                                    >
                                        <div
                                            className="rounded-full aspect-square h-4"
                                            style={{
                                                background:
                                                    point.dataset.borderColor,
                                            }}
                                        />

                                        <span>{point.dataset.label} : </span>

                                        <span className="font-bold">
                                            {new Intl.NumberFormat('fr-FR', {
                                                maximumFractionDigits: 0,
                                            }).format(point.raw)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {filteredData.length <= 1 && (
                <div className="aspect-[3] flex items-center justify-center text-center bg-gray-50 border text-gray-400 text-sm">
                    Pas assez de données dans l‘historique
                </div>
            )}

            {filteredData.length > 1 && (
                <div className="flex flex-col mt-5">
                    <span className="font-medium">Projection</span>

                    <span className="text-pink-500">
                        {dateWhereStockIsZero && (
                            <>
                                <span>
                                    {dayjs(dateWhereStockIsZero).diff(
                                        dayjs(),
                                        'day'
                                    )}
                                    {' jours restants'}

                                    {' - ' +
                                        dayjs(dateWhereStockIsZero).format(
                                            'll'
                                        )}
                                </span>
                                <br />
                            </>
                        )}

                        {!dateWhereStockIsZero && (
                            <span>Stock supérieur à 6 mois</span>
                        )}
                    </span>

                    {dateWhereAssemblableStockIsZero && (
                        <span className="text-yellow-500">
                            {dayjs(dateWhereAssemblableStockIsZero).diff(
                                dayjs(),
                                'day'
                            )}
                            {' jours restants avec le stock assemblable'}
                            {' - ' +
                                dayjs(dateWhereAssemblableStockIsZero).format(
                                    'll'
                                )}
                        </span>
                    )}
                </div>
            )}

            {false && component.children.length > 0 && locationIdNum === 1 && (
                <div className="flex flex-col mt-5 items-start gap-2">
                    <span className="font-medium">Assemblage</span>
                    <Button variant="default">Programmer un assemblage</Button>
                </div>
            )}

            {false && (
                <pre>
                    {JSON.stringify(
                        invprojection({ component: component }),
                        null,
                        2
                    )}
                </pre>
            )}
        </Box>
    )
}
